import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import {
  drawPdfBrandHeaderRow,
  drawPdfPageFooters,
  loadUnityPdfLogo,
  PDF_BRAND_TO_TITLE_GAP,
  PDF_FOOTER_ZONE,
  PDF_PAGE_MARGIN,
  toPdfText,
} from '@/utils/pdf-brand-header';
import { ensurePdfFonts, pdfFont } from '@/utils/pdf-fonts';

type JsPdfWithAutoTable = jsPDF & {
  lastAutoTable?: { finalY: number };
};

export interface BillingInvoiceDocument {
  churchName: string;
  churchLocale: string;
  churchTimezone: string;
  invoiceNumber: number;
  status: string;
  plan: string;
  interval: string;
  seatCount: number;
  periodStart: string;
  periodEnd: string;
  subtotalMinor: number;
  discountMinor: number;
  taxMinor: number;
  totalMinor: number;
  currency: string;
  couponCode: string | null;
  issuedAt: string;
  dueAt: string | null;
  paidAt: string | null;
}

export interface BillingReceiptDocument {
  churchName: string;
  churchLocale: string;
  churchTimezone: string;
  receiptNumber: number;
  invoiceNumber: number;
  amountMinor: number;
  currency: string;
  issuedAt: string;
}

export interface BillingDocumentPdfLabels {
  merchantName: string;
  merchantDescriptor: string;
  merchantCompany: string;
  merchantContact: string;
  merchantAddress: string;
  invoiceTitle: string;
  invoiceNumber: string;
  billTo: string;
  issued: string;
  due: string;
  paid: string;
  status: string;
  period: string;
  description: string;
  seats: string;
  amount: string;
  planLine: string;
  seatsLine: string;
  coupon: string;
  subtotal: string;
  discount: string;
  tax: string;
  total: string;
  amountPaid: string;
  amountDue: string;
  receiptTitle: string;
  receiptNumber: string;
  receivedFrom: string;
  invoiceLabel: string;
  invoiceRef: string;
  amountPaidLabel: string;
  subscriptionPayment: string;
  thankYou: string;
  statuses: Record<string, string>;
}

const formatDocumentDate = (iso: string, timezone: string, locale: string): string =>
  new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: timezone,
  }).format(new Date(iso));

const drawRule = (doc: jsPDF, y: number, pageWidth: number) => {
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);
  doc.line(PDF_PAGE_MARGIN, y, pageWidth - PDF_PAGE_MARGIN, y);
};

const drawMetaPair = (
  doc: jsPDF,
  x: number,
  y: number,
  width: number,
  label: string,
  value: string,
): number => {
  doc.setFont(pdfFont(), 'normal');
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text(toPdfText(label), x, y);
  doc.setFont(pdfFont(), 'bold');
  doc.setFontSize(10);
  doc.setTextColor(40, 40, 40);
  const lines = doc.splitTextToSize(toPdfText(value), width);
  doc.text(lines, x, y + 5);
  return y + 5 + lines.length * 4.5 + 3;
};

const drawMetaRow = (
  doc: jsPDF,
  y: number,
  leftX: number,
  pairWidth: number,
  gap: number,
  left: { label: string; value: string },
  right?: { label: string; value: string },
): number => {
  const leftBottom = drawMetaPair(doc, leftX, y, pairWidth, left.label, left.value);
  if (!right) return leftBottom;
  const rightBottom = drawMetaPair(doc, leftX + pairWidth + gap, y, pairWidth, right.label, right.value);
  return Math.max(leftBottom, rightBottom);
};

const drawMoneyTotals = (
  doc: jsPDF,
  pageWidth: number,
  startY: number,
  rows: { label: string; value: string; emphasis?: boolean }[],
): number => {
  const labelX = pageWidth - PDF_PAGE_MARGIN - 72;
  const valueX = pageWidth - PDF_PAGE_MARGIN;
  let y = startY;

  for (const row of rows) {
    doc.setFont(pdfFont(), row.emphasis ? 'bold' : 'normal');
    doc.setFontSize(row.emphasis ? 11 : 9);
    doc.setTextColor(40, 40, 40);
    doc.text(toPdfText(row.label), labelX, y, { align: 'right' });
    doc.text(toPdfText(row.value), valueX, y, { align: 'right' });
    y += row.emphasis ? 7 : 5.5;
  }
  return y;
};

const createDocument = async (
  merchantName: string,
  merchantDescriptor: string,
  merchantCompany: string,
  merchantContact: string,
  merchantAddress: string,
) => {
  const logo = await loadUnityPdfLogo();
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' }) as JsPdfWithAutoTable;
  await ensurePdfFonts(doc);
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contactWidth = pageWidth - PDF_PAGE_MARGIN * 2 - 60;

  doc.setFillColor(10, 98, 255);
  doc.rect(0, 0, pageWidth, 2.5, 'F');

  const brand = drawPdfBrandHeaderRow(doc, pageWidth, PDF_PAGE_MARGIN, {
    churchName: merchantName,
    branchName: merchantDescriptor,
    logo,
  });

  let contactY = brand.leftBottomY + 2;
  doc.setFont(pdfFont(), 'normal');
  doc.setFontSize(8);
  doc.setTextColor(40, 40, 40);
  const companyLines = doc.splitTextToSize(toPdfText(merchantCompany), contactWidth);
  doc.text(companyLines, PDF_PAGE_MARGIN, contactY);
  contactY += companyLines.length * 3.6;

  doc.setTextColor(100, 100, 100);
  for (const line of [merchantContact, merchantAddress]) {
    const wrapped = doc.splitTextToSize(toPdfText(line), contactWidth);
    doc.text(wrapped, PDF_PAGE_MARGIN, contactY);
    contactY += wrapped.length * 3.6;
  }

  return {
    doc,
    pageWidth,
    pageHeight,
    brand: {
      ...brand,
      leftBottomY: contactY,
      blockBottomY: Math.max(brand.blockBottomY, contactY),
    },
  };
};

export const buildInvoicePdfBlob = async (
  document: BillingInvoiceDocument,
  labels: BillingDocumentPdfLabels,
  formatMoney: (amountMinor: number, currency: string, locale: string) => string,
): Promise<Blob> => {
  const { doc, pageWidth, pageHeight, brand } = await createDocument(
    labels.merchantName,
    labels.merchantDescriptor,
    labels.merchantCompany,
    labels.merchantContact,
    labels.merchantAddress,
  );
  const locale = document.churchLocale || 'en';
  const timezone = document.churchTimezone || 'UTC';
  const contentWidth = pageWidth - PDF_PAGE_MARGIN * 2;
  const columnWidth = (contentWidth - 8) / 2;

  let y = brand.leftBottomY + PDF_BRAND_TO_TITLE_GAP;
  doc.setFont(pdfFont(), 'bold');
  doc.setFontSize(16);
  doc.setTextColor(40, 40, 40);
  doc.text(toPdfText(labels.invoiceTitle), PDF_PAGE_MARGIN, y);
  y += 6;
  doc.setFont(pdfFont(), 'normal');
  doc.setFontSize(10);
  doc.setTextColor(80, 80, 80);
  doc.text(toPdfText(labels.invoiceNumber), PDF_PAGE_MARGIN, y);
  y = Math.max(y, brand.blockBottomY) + 4;
  drawRule(doc, y, pageWidth);
  y += 8;

  let leftY = drawMetaPair(
    doc,
    PDF_PAGE_MARGIN,
    y,
    columnWidth,
    labels.billTo,
    document.churchName,
  );
  leftY = drawMetaPair(
    doc,
    PDF_PAGE_MARGIN,
    leftY,
    columnWidth,
    labels.period,
    `${formatDocumentDate(document.periodStart, timezone, locale)} - ${formatDocumentDate(document.periodEnd, timezone, locale)}`,
  );

  const rightX = PDF_PAGE_MARGIN + columnWidth + 8;
  const pairGap = 4;
  const pairWidth = (columnWidth - pairGap) / 2;
  let rightY = drawMetaRow(
    doc,
    y,
    rightX,
    pairWidth,
    pairGap,
    { label: labels.issued, value: formatDocumentDate(document.issuedAt, timezone, locale) },
    {
      label: labels.due,
      value: document.dueAt ? formatDocumentDate(document.dueAt, timezone, locale) : '—',
    },
  );
  rightY = drawMetaRow(
    doc,
    rightY,
    rightX,
    pairWidth,
    pairGap,
    { label: labels.status, value: labels.statuses[document.status] ?? document.status },
    {
      label: labels.paid,
      value: document.paidAt ? formatDocumentDate(document.paidAt, timezone, locale) : '—',
    },
  );

  y = Math.max(leftY, rightY) + 2;

  const description = [
    labels.planLine,
    labels.seatsLine,
    document.couponCode ? labels.coupon : null,
  ]
    .filter((line): line is string => Boolean(line))
    .join('\n');

  autoTable(doc, {
    startY: y,
    head: [[toPdfText(labels.description), toPdfText(labels.seats), toPdfText(labels.amount)]],
    body: [[
      toPdfText(description),
      toPdfText(String(document.seatCount)),
      toPdfText(formatMoney(document.subtotalMinor, document.currency, locale)),
    ]],
    theme: 'grid',
    styles: {
      font: pdfFont(),
      fontSize: 9,
      textColor: [40, 40, 40],
      lineColor: [220, 220, 220],
      lineWidth: 0.1,
      valign: 'top',
      cellPadding: { top: 3, right: 3, bottom: 3, left: 3 },
      overflow: 'linebreak',
    },
    headStyles: {
      font: pdfFont(),
      fontStyle: 'bold',
      fillColor: [245, 245, 245],
      textColor: [40, 40, 40],
    },
    columnStyles: {
      0: { cellWidth: contentWidth * 0.56 },
      1: { cellWidth: contentWidth * 0.16, halign: 'right' },
      2: { cellWidth: contentWidth * 0.28, halign: 'right' },
    },
    margin: {
      left: PDF_PAGE_MARGIN,
      right: PDF_PAGE_MARGIN,
      bottom: PDF_PAGE_MARGIN + PDF_FOOTER_ZONE,
    },
  });

  y = (doc.lastAutoTable?.finalY ?? y) + 8;
  const totals: { label: string; value: string; emphasis?: boolean }[] = [
    {
      label: labels.subtotal,
      value: formatMoney(document.subtotalMinor, document.currency, locale),
    },
  ];
  if (document.discountMinor > 0) {
    totals.push({
      label: labels.discount,
      value: formatMoney(-document.discountMinor, document.currency, locale),
    });
  }
  if (document.taxMinor > 0) {
    totals.push({
      label: labels.tax,
      value: formatMoney(document.taxMinor, document.currency, locale),
    });
  }
  totals.push({
    label: labels.total,
    value: formatMoney(document.totalMinor, document.currency, locale),
    emphasis: true,
  });
  totals.push({
    label: labels.amountDue,
    value: formatMoney(document.totalMinor, document.currency, locale),
  });

  drawMoneyTotals(doc, pageWidth, y, totals);
  drawPdfPageFooters(doc, pageWidth, pageHeight, labels.merchantName);
  return doc.output('blob');
};

export const buildReceiptPdfBlob = async (
  document: BillingReceiptDocument,
  labels: BillingDocumentPdfLabels,
  formatMoney: (amountMinor: number, currency: string, locale: string) => string,
): Promise<Blob> => {
  const { doc, pageWidth, pageHeight, brand } = await createDocument(
    labels.merchantName,
    labels.merchantDescriptor,
    labels.merchantCompany,
    labels.merchantContact,
    labels.merchantAddress,
  );
  const locale = document.churchLocale || 'en';
  const timezone = document.churchTimezone || 'UTC';
  const contentWidth = pageWidth - PDF_PAGE_MARGIN * 2;
  const columnWidth = (contentWidth - 8) / 2;

  let y = brand.leftBottomY + PDF_BRAND_TO_TITLE_GAP;
  doc.setFont(pdfFont(), 'bold');
  doc.setFontSize(16);
  doc.setTextColor(40, 40, 40);
  doc.text(toPdfText(labels.receiptTitle), PDF_PAGE_MARGIN, y);
  y += 6;
  doc.setFont(pdfFont(), 'normal');
  doc.setFontSize(10);
  doc.setTextColor(80, 80, 80);
  doc.text(toPdfText(labels.receiptNumber), PDF_PAGE_MARGIN, y);
  y = Math.max(y, brand.blockBottomY) + 4;
  drawRule(doc, y, pageWidth);
  y += 8;

  const leftBottom = drawMetaPair(
    doc,
    PDF_PAGE_MARGIN,
    y,
    columnWidth,
    labels.receivedFrom,
    document.churchName,
  );
  let rightY = y;
  rightY = drawMetaPair(
    doc,
    PDF_PAGE_MARGIN + columnWidth + 8,
    rightY,
    columnWidth,
    labels.issued,
    formatDocumentDate(document.issuedAt, timezone, locale),
  );
  rightY = drawMetaPair(
    doc,
    PDF_PAGE_MARGIN + columnWidth + 8,
    rightY,
    columnWidth,
    labels.invoiceLabel,
    labels.invoiceRef,
  );
  y = Math.max(leftBottom, rightY) + 2;

  autoTable(doc, {
    startY: y,
    head: [[toPdfText(labels.description), toPdfText(labels.amount)]],
    body: [[
      toPdfText(`${labels.subscriptionPayment}\n${labels.invoiceRef}`),
      toPdfText(formatMoney(document.amountMinor, document.currency, locale)),
    ]],
    theme: 'grid',
    styles: {
      font: pdfFont(),
      fontSize: 9,
      textColor: [40, 40, 40],
      lineColor: [220, 220, 220],
      lineWidth: 0.1,
      valign: 'top',
      cellPadding: { top: 3, right: 3, bottom: 3, left: 3 },
      overflow: 'linebreak',
    },
    headStyles: {
      font: pdfFont(),
      fontStyle: 'bold',
      fillColor: [245, 245, 245],
      textColor: [40, 40, 40],
    },
    columnStyles: {
      0: { cellWidth: contentWidth * 0.68 },
      1: { cellWidth: contentWidth * 0.32, halign: 'right' },
    },
    margin: {
      left: PDF_PAGE_MARGIN,
      right: PDF_PAGE_MARGIN,
      bottom: PDF_PAGE_MARGIN + PDF_FOOTER_ZONE,
    },
  });

  y = (doc.lastAutoTable?.finalY ?? y) + 8;
  drawMoneyTotals(doc, pageWidth, y, [
    {
      label: labels.paid,
      value: formatMoney(document.amountMinor, document.currency, locale),
      emphasis: true,
    },
  ]);
  y += 12;
  doc.setFont(pdfFont(), 'normal');
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  doc.text(toPdfText(labels.thankYou), PDF_PAGE_MARGIN, y);

  drawPdfPageFooters(doc, pageWidth, pageHeight, labels.merchantName);
  return doc.output('blob');
};
